import { useCallback, useEffect, useMemo, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { isAuthConfigured, supabase } from "./supabase";

export interface AuthState {
  ready: boolean;
  configured: boolean;
  user: User | null;
  session: Session | null;
  sendMagicLink: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  error: string | null;
}

export function useAuth(): AuthState {
  const [ready, setReady] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState<string | null>(null);
  const configured = useMemo(() => isAuthConfigured(), []);

  useEffect(() => {
    const client = supabase();
    if (!client) {
      setReady(true);
      return;
    }

    let active = true;
    void client.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session ?? null);
      setReady(true);
    });

    const { data: listener } = client.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setReady(true);
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const sendMagicLink = useCallback(async (email: string) => {
    setError(null);
    const client = supabase();
    if (!client) throw new Error("Accounts are not configured on this deployment.");
    const { error: failure } = await client.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (failure) throw new Error(failure.message);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    const client = supabase();
    if (!client) throw new Error("Accounts are not configured on this deployment.");
    const { error: failure } = await client.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    if (failure) throw new Error(failure.message);
  }, []);

  const signOut = useCallback(async () => {
    const client = supabase();
    if (!client) return;
    await client.auth.signOut();
    setSession(null);
  }, []);

  return {
    ready,
    configured,
    user: session?.user ?? null,
    session,
    sendMagicLink,
    signInWithGoogle,
    signOut,
    error,
  };
}
